import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Radio, Row, Select } from 'antd';
import RowSection from '../atoms/RowSection';
import styled from 'styled-components';
import { FormInstance } from 'antd/lib/form';
import { ProfessionalDocumentsRequest } from '../../interfaces/ProfessionalDocumentsRequest';
import { State } from '../../interfaces/State';
import personalDataRepo from '../../services/repository/personalData';
import { ProfessionalOrganization } from '../../interfaces/ProfessionalOrganization';
import addressRepo from '../../services/repository/address';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { AdmissionDataRules } from '../../services/util/field-rules-manager/types/fieldRuleTypes';
import AdmissionFormItem from '../molecules/AdmissionFormItem';
import { WorkCardTypes } from '../../services/util/field-rules-manager/enums/WorkCardType';
import { events } from '../../services/util/field-rules-manager/FieldRuleService';
import { pisPasepNormalizers } from '../../services/util/field-rules-manager/normalizers';
import { isScreenUnderWidth } from '../../services/util/screen';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Option } = Select;

const ButtonCol = styled(Col)`
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
`;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

type Props = {
  handleNext: () => void,
  initForm: ProfessionalDocumentsRequest | undefined,
  admissionDataRules: AdmissionDataRules
};

const ProfessionalDocuments = React.forwardRef<FormInstance, Props>(({ handleNext, initForm, admissionDataRules }, ref) => {
  const [form] = Form.useForm<ProfessionalDocumentsRequest | undefined>();
  const formRules = admissionDataRules.professionalDocuments;
  const currentDate = dayjs();

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    getFieldsValue: form.getFieldsValue,
    submit: () => form.submit(),
  }));

  const [data, setData] = useState<{
    professionalOrganization: ProfessionalOrganization[],
    states: State[],
  }>({ professionalOrganization: [], states: [] });

  const fetchData = async () => {
    try {
      const [professionalOrganizationResponse, statesResponse] = await Promise.all([
        personalDataRepo.findAllProfessionalOrganization(),
        addressRepo.findAllStates(),
      ]);

      setData({
        professionalOrganization: professionalOrganizationResponse.data,
        states: statesResponse.data,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };


  const onValuesChangeHandler = (changedValues: any, values: ProfessionalDocumentsRequest | undefined) => {
    const watchedFields = [
      formRules.workCardType?.name,
      formRules.workCardNumber?.name,
      formRules.specialWorkClassNumber?.name,
    ];

    if (Object.keys(changedValues).some(changedField => watchedFields.includes(changedField))) {
      events.triggerFormRuleUpdateEvent();
    }
  };

  useEffect(() => {
    fetchData().then(r => r);
  }, []);

  useEffect(() => {
    if (initForm && initForm.workCardIssuanceDate != null) {
      initForm.workCardIssuanceDate = dayjs(initForm.workCardIssuanceDate);
    }
    if (initForm && initForm.specialWorkClassIssuanceDate != null) {
      initForm.specialWorkClassIssuanceDate = dayjs(initForm.specialWorkClassIssuanceDate);
    }
    if (initForm && initForm.specialWorkClassValidityDate != null) {
      initForm.specialWorkClassValidityDate = dayjs(initForm.specialWorkClassValidityDate);
    }
    form.setFieldsValue(initForm);

  }, [initForm]);

  const isUnder = isScreenUnderWidth(576);

  return (
    <>
      <Form form={form} {...formItemLayout} style={{ maxWidth: 600 }} requiredMark={false} layout='vertical' onValuesChange={onValuesChangeHandler}>
        <Row><RowSection>Carteira de Trabalho e Previdência Social (CTPS)</RowSection></Row>

        <Row>
          <AdmissionFormItem fieldRule={formRules.workCardType}>
            <Radio.Group >
              <Radio value={WorkCardTypes.PHYSICAL}>Físico</Radio>
              <Radio value={WorkCardTypes.DIGITAL}>Digital</Radio>
            </Radio.Group>
          </AdmissionFormItem>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.workCardNumber}>
              <Input />
            </AdmissionFormItem>
          </Col>


          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.workCardSeries}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.workCardState}>
              <Select
                placeholder='Selecione uma opção'
                popupMatchSelectWidth={false}
                showSearch
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                optionFilterProp='children'
              >
                {data.states.map((state) => (
                  <Option key={`workCardState-${state.id}`} value={state.id}>
                    {state.id}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.workCardIssuanceDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row><RowSection>PIS/PASEP</RowSection></Row>

        <Row>
          <Col xs={24} sm={24} md={16} lg={16}>
            <AdmissionFormItem fieldRule={formRules.pisNumber} formItemProps={{normalize:pisPasepNormalizers.mask}}>
              <Input />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row><RowSection>Orgão de Classe</RowSection></Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.specialWorkClassNumber}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {data.professionalOrganization.map((organization) => (
                  <Option
                    key={`specialWorkClassNumber-${organization.id}`}
                    value={organization.id}
                  >
                    {organization.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.specialWorkClassEnrollment}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.specialWorkClassIssuanceDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.specialWorkClassValidityDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>
        </Row>
      </Form>

      <Row>
        <ButtonCol span={24}> <Button type={'primary'}
          onClick={() => handleNext()}>Prosseguir</Button></ButtonCol>
      </Row>
    </>
  );
});

ProfessionalDocuments.displayName = 'ProfessionalDocuments';

export default ProfessionalDocuments;
